// ─────────────────────────────────────────────────────────────────────────────
// Voice Mode 2.0 — Phase P0 "Visible Spine": UI control target registry.
//
// Spec: docs/plans/voice-mode-ui-control-protocol_v1_2026-09-19.md §4
// Every actionable DOM node the agent may drive declares a stable
// `data-agent-id`.  Resolution is LAZY (querySelector at execution time) so it
// survives React re-renders; the catalog below doubles as the capability
// manifest handed to the planner (P2) and drives contract tests that catch
// drift when pages are refactored.
//
// Naming convention: `<area>.<object>[.<action>]` (e.g. tasks.form.submit).
// Dynamic collections use the entity id: `tasks.row.<uuid>`.
//
// 2026-09-21 (owner): the catalog alone is NOT enough — most pages carry no
// tags, so the agent could not reach controls such as the Opportunity Scanner's
// Refresh button. A runtime tagger (`agentTag.ts`) now derives a stable id for
// every interactive element, and `resolveAgentId` falls back to it. Explicit
// `data-agent-id` still wins everywhere it is set.
// ─────────────────────────────────────────────────────────────────────────────

import { resolveAutoId } from './agentTag';

export type UiTargetKind = 'button' | 'input' | 'link' | 'nav' | 'toggle' | 'container' | 'row';

/** Executor kinds shipped in P0 (fill/select/focus/openPanel/toast come with P2). */
export type UiStepKind = 'navigate' | 'click' | 'highlight' | 'scrollTo' | 'type' | 'select' | 'request' | 'today';

export interface UiTargetDescriptor {
  /** Stable data-agent-id (or a `.*` wildcard prefix for dynamic families). */
  id: string;
  /** Human label for the capability manifest + planner. */
  label: string;
  /** Route where the target lives ('*' = any app route, e.g. sidebar nav). */
  page: string;
  kind: UiTargetKind;
  /** Which executor kinds may act on this target. */
  actions: UiStepKind[];
  /** Fallback selector used when the data-agent-id attribute is missing. */
  selector?: string;
  /** True when ids in this family carry a runtime entity suffix. */
  dynamic?: boolean;
  /** True for destructive/publishing/financial controls: the server refuses to
   *  act on them unless the caller confirmed (WCP §1.3 reject-by-default). */
  dangerous?: boolean;
}

/** The ~20 P0 hot targets (sidebar nav, tasks, projects, calendar, chat,
 * assistant widget, voice toggles). */
export const UI_TARGETS: readonly UiTargetDescriptor[] = [
  // Sidebar navigation (app-wide)
  { id: 'nav.opportunity-scanner', label: 'Opportunity Scanner', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.autonomy', label: 'Autonomy Control', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.chat', label: 'SAIK Chat', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.dashboard', label: 'Dashboard', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.tasks', label: 'Tasks', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.projects', label: 'Projects', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.calendar', label: 'Calendar', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.email', label: 'Email', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.files', label: 'Files', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.sites', label: 'My Sites', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.knowledge-base', label: 'Knowledge Base', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'nav.graphs', label: 'Knowledge Graph', page: '*', kind: 'nav', actions: ['click', 'highlight', 'scrollTo'] },

  // Tasks
  { id: 'tasks.new', label: 'New Task button', page: '/app/tasks', kind: 'button', actions: ['click', 'highlight', 'scrollTo'], selector: 'button[aria-label="New task"]' },
  { id: 'tasks.filter.*', label: 'Status filter tab', page: '/app/tasks', kind: 'toggle', actions: ['click', 'highlight', 'scrollTo'], dynamic: true },
  { id: 'tasks.row.*', label: 'Task row', page: '/app/tasks', kind: 'row', actions: ['click', 'highlight', 'scrollTo'], dynamic: true },

  // Projects
  { id: 'projects.new', label: 'New Project button', page: '/app/projects', kind: 'button', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'projects.archived-toggle', label: 'Show archived toggle', page: '/app/projects', kind: 'toggle', actions: ['click', 'highlight'] },
  { id: 'projects.row.*', label: 'Project row', page: '/app/projects', kind: 'row', actions: ['click', 'highlight', 'scrollTo'], dynamic: true },

  // Calendar
  { id: 'calendar.new', label: 'New Event button', page: '/app/calendar', kind: 'button', actions: ['click', 'highlight', 'scrollTo'] },
  { id: 'calendar.week', label: 'Calendar week header', page: '/app/calendar', kind: 'container', actions: ['highlight', 'scrollTo'] },
  { id: 'calendar.today', label: 'Today button', page: '/app/calendar', kind: 'button', actions: ['click', 'highlight'] },

  // Chat page composer
  { id: 'chat.composer', label: 'Chat message composer', page: '/app/chat', kind: 'input', actions: ['highlight', 'scrollTo'] },
  { id: 'chat.mic', label: 'Chat speak button', page: '/app/chat', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'chat.send', label: 'Chat send button', page: '/app/chat', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'chat.voice-toggle', label: 'Read replies aloud toggle', page: '/app/chat', kind: 'toggle', actions: ['click', 'highlight'] },
  { id: 'chat.hands-free', label: 'Hands-free replies toggle', page: '/app/chat', kind: 'toggle', actions: ['click', 'highlight'] },

  // Assistant widget (app-wide)
  { id: 'widget.composer', label: 'Assistant message input', page: '*', kind: 'input', actions: ['highlight'] },
  { id: 'widget.mic', label: 'Assistant speak button', page: '*', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'widget.send', label: 'Assistant send button', page: '*', kind: 'button', actions: ['click', 'highlight'] },

  // Voice toggles (Settings → Virtual Assistant)
  { id: 'voice.settings.read-aloud', label: 'Read AI replies aloud', page: '/app/settings/virtual-assistant', kind: 'toggle', actions: ['click', 'highlight'] },
  { id: 'voice.settings.auto-listen', label: 'Hands-free replies', page: '/app/settings/virtual-assistant', kind: 'toggle', actions: ['click', 'highlight'] },
  { id: 'voice.settings.activation', label: 'Voice activation (iOS)', page: '/app/settings/virtual-assistant', kind: 'toggle', actions: ['click', 'highlight'] },
  { id: 'assistant.settings.second-mind', label: 'Thinking mind toggle', page: '/app/settings/virtual-assistant', kind: 'toggle', actions: ['click', 'highlight'] },

  // Tasks — forms, notes, row actions
  { id: 'tasks.form.title', label: 'Task title field', page: '/app/tasks', kind: 'input', actions: ['type', 'highlight', 'scrollTo'] },
  { id: 'tasks.form.execution-prompt', label: 'Agent execution prompt field', page: '/app/tasks', kind: 'input', actions: ['type', 'highlight', 'scrollTo'] },
  { id: 'tasks.form.description', label: 'Task description field', page: '/app/tasks', kind: 'input', actions: ['type', 'highlight', 'scrollTo'] },
  { id: 'tasks.form.project', label: 'Task project select', page: '/app/tasks', kind: 'input', actions: ['select', 'highlight', 'scrollTo'] },
  { id: 'tasks.form.checkpoint', label: 'Task roadmap phase select', page: '/app/tasks', kind: 'input', actions: ['select', 'highlight', 'scrollTo'] },
  { id: 'tasks.form.status', label: 'Task status select', page: '/app/tasks', kind: 'input', actions: ['select', 'highlight', 'scrollTo'] },
  { id: 'tasks.form.submit', label: 'Save task button', page: '/app/tasks', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'tasks.notes.input', label: 'Add note field', page: '/app/tasks', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'tasks.notes.add', label: 'Add note button', page: '/app/tasks', kind: 'button', actions: ['click', 'highlight'] },

  // Projects — forms, phases, row actions
  { id: 'projects.form.name', label: 'Project name field', page: '/app/projects', kind: 'input', actions: ['type', 'highlight', 'scrollTo'] },
  { id: 'projects.form.description', label: 'Project description field', page: '/app/projects', kind: 'input', actions: ['type', 'highlight', 'scrollTo'] },
  { id: 'projects.form.submit', label: 'Save project button', page: '/app/projects', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'projects.phase.title', label: 'Phase title field', page: '/app/projects', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'projects.phase.description', label: 'Phase description field', page: '/app/projects', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'projects.phase.submit', label: 'Add phase button', page: '/app/projects', kind: 'button', actions: ['click', 'highlight'] },

  // Calendar form
  { id: 'calendar.form.title', label: 'Event title field', page: '/app/calendar', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'calendar.form.description', label: 'Event description field', page: '/app/calendar', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'calendar.form.start', label: 'Event start field', page: '/app/calendar', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'calendar.form.end', label: 'Event end field', page: '/app/calendar', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'calendar.form.type', label: 'Event type select', page: '/app/calendar', kind: 'input', actions: ['select', 'highlight'] },
  { id: 'calendar.form.submit', label: 'Create event button', page: '/app/calendar', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'calendar.day.*', label: 'Calendar day cell', page: '/app/calendar', kind: 'container', actions: ['click', 'highlight', 'scrollTo'], dynamic: true },

  // Files (ContentAssets)
  { id: 'files.upload', label: 'Upload media button', page: '/app/files', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'files.generate-video', label: 'Generate video button', page: '/app/files', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'files.new-folder', label: 'New folder button', page: '/app/files', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'files.folder.name', label: 'Folder name field', page: '/app/files', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'files.folder.create', label: 'Create folder button', page: '/app/files', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'files.workspace-filter', label: 'Workspace filter', page: '/app/files', kind: 'input', actions: ['select', 'highlight'] },
  { id: 'files.asset.*', label: 'File asset', page: '/app/files', kind: 'row', actions: ['click', 'highlight', 'scrollTo'], dynamic: true },
  { id: 'files.folder.*', label: 'File folder', page: '/app/files', kind: 'row', actions: ['click', 'highlight'], dynamic: true },

  // My Sites
  { id: 'sites.new', label: 'New site button', page: '/app/sites', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'sites.form.name', label: 'Site name field', page: '/app/sites', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'sites.form.workspace', label: 'Site workspace select', page: '/app/sites', kind: 'input', actions: ['select', 'highlight'] },
  { id: 'sites.form.description', label: 'Site description field', page: '/app/sites', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'sites.form.entry-point', label: 'Site entry point field', page: '/app/sites', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'sites.form.submit', label: 'Create site button', page: '/app/sites', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'sites.row.*', label: 'Site row', page: '/app/sites', kind: 'row', actions: ['click', 'highlight'], dynamic: true },

  // Knowledge Graph
  { id: 'graphs.persist-new', label: 'Persist graph button', page: '/app/graphs', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'graphs.refresh', label: 'Refresh graphs button', page: '/app/graphs', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'graphs.create', label: 'Create graph button', page: '/app/graphs', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'graphs.json-input', label: 'Graph JSON input', page: '/app/graphs', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'graphs.row.*', label: 'Graph row', page: '/app/graphs', kind: 'row', actions: ['click', 'highlight'], dynamic: true },

  // Email
  { id: 'email.compose', label: 'Compose email button', page: '/app/email/inbox', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'email.triage', label: 'Run triage button', page: '/app/email/inbox', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'email.sync', label: 'Sync inbox button', page: '/app/email/inbox', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'email.tab.*', label: 'Email tab', page: '/app/email/inbox', kind: 'toggle', actions: ['click', 'highlight'], dynamic: true },
  { id: 'email.category.*', label: 'Email category filter', page: '/app/email/inbox', kind: 'toggle', actions: ['click', 'highlight'], dynamic: true },
  { id: 'email.account-select', label: 'Email account select', page: '/app/email/inbox', kind: 'input', actions: ['select', 'highlight'] },
  { id: 'email.reply.*', label: 'Reply action', page: '/app/email/inbox', kind: 'button', actions: ['click', 'highlight'], dynamic: true },
  { id: 'email.compose.*', label: 'Compose field', page: '/app/email/inbox', kind: 'input', actions: ['type', 'select', 'click', 'highlight'], dynamic: true },

  // Social composer
  { id: 'social.composer.title', label: 'Post title field', page: '/app/social/composer', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'social.composer.content', label: 'Post content field', page: '/app/social/composer', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'social.composer.media', label: 'Post media URLs field', page: '/app/social/composer', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'social.composer.schedule', label: 'Post schedule field', page: '/app/social/composer', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'social.composer.submit', label: 'Save/publish post button', page: '/app/social/composer', kind: 'button', actions: ['click', 'highlight'] },
  // Trading (dangerous financial controls — confirm-gated per WCP §1.3)
  { id: 'trading.orders.new', label: 'New order button', page: '/app/trading/orders', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'trading.orders.filter.*', label: 'Order status filter', page: '/app/trading/orders', kind: 'toggle', actions: ['click', 'highlight'], dynamic: true },
  { id: 'trading.orders.row.*', label: 'Order row (cancel)', page: '/app/trading/orders', kind: 'row', actions: ['click', 'highlight'], dynamic: true, dangerous: true },
  { id: 'trading.order.symbol', label: 'Order symbol field', page: '/app/trading/orders', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'trading.order.side', label: 'Order side select', page: '/app/trading/orders', kind: 'input', actions: ['select', 'highlight'] },
  { id: 'trading.order.qty', label: 'Order quantity field', page: '/app/trading/orders', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'trading.order.submit', label: 'Place order button', page: '/app/trading/orders', kind: 'button', actions: ['click', 'highlight'], dangerous: true },

  // Email send/delete (dangerous — confirm-gated)
  { id: 'email.compose.send', label: 'Send email button', page: '/app/email/inbox', kind: 'button', actions: ['click', 'highlight'], dangerous: true },
  { id: 'email.compose.to', label: 'Compose recipient field', page: '/app/email/inbox', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'email.compose.subject', label: 'Compose subject field', page: '/app/email/inbox', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'email.compose.body', label: 'Compose body field', page: '/app/email/inbox', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'email.reply.send', label: 'Send reply button', page: '/app/email/inbox', kind: 'button', actions: ['click', 'highlight'], dangerous: true },
  { id: 'email.reply.input', label: 'Reply text field', page: '/app/email/inbox', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'email.reply.archive', label: 'Archive message button', page: '/app/email/inbox', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'email.reply.delete', label: 'Delete message button', page: '/app/email/inbox', kind: 'button', actions: ['click', 'highlight'], dangerous: true },
  { id: 'email.accounts.add', label: 'Add email account button', page: '/app/email/accounts', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'email.accounts.form.submit', label: 'Verify and connect button', page: '/app/email/accounts', kind: 'button', actions: ['click', 'highlight'], dangerous: true },

  // Social accounts (connect/delete — credential-adjacent)
  { id: 'social.accounts.row.*', label: 'Social account row', page: '/app/social/accounts', kind: 'row', actions: ['click', 'highlight'], dynamic: true },

  // Sites publishing (public-facing — confirm-gated)
  { id: 'sites.row.*.publish', label: 'Publish site preview button', page: '/app/sites', kind: 'button', actions: ['click', 'highlight'], dynamic: true, dangerous: true },
  { id: 'sites.row.*.unpublish', label: 'Unpublish site button', page: '/app/sites', kind: 'button', actions: ['click', 'highlight'], dynamic: true },

  // JobBot / Content / Marketplace forms
  { id: 'jobbot.sites.new', label: 'New job site button', page: '/app/customer-portal/jobsites', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'jobbot.sites.form.name', label: 'Job site name field', page: '/app/customer-portal/jobsites', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'jobbot.sites.form.url', label: 'Job site URL field', page: '/app/customer-portal/jobsites', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'jobbot.sites.form.query', label: 'Job site query field', page: '/app/customer-portal/jobsites', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'jobbot.sites.form.submit', label: 'Save job site button', page: '/app/customer-portal/jobsites', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'jobbot.leads.new', label: 'New lead button', page: '/app/jobbot/leads', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'jobbot.leads.search', label: 'Lead search field', page: '/app/jobbot/leads', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'jobbot.leads.form.title', label: 'Lead title field', page: '/app/jobbot/leads', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'jobbot.leads.form.company', label: 'Lead company field', page: '/app/jobbot/leads', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'jobbot.leads.form.submit', label: 'Save lead button', page: '/app/jobbot/leads', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'jobbot.applications.new', label: 'New application button', page: '/app/jobbot/applications', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'jobbot.applications.form.submit', label: 'Save application button', page: '/app/jobbot/applications', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'jobbot.resumes.new', label: 'New resume button', page: '/app/jobbot/resumes', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'jobbot.resumes.form.title', label: 'Resume title field', page: '/app/jobbot/resumes', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'jobbot.resumes.form.content', label: 'Resume content field', page: '/app/jobbot/resumes', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'jobbot.resumes.form.submit', label: 'Save resume button', page: '/app/jobbot/resumes', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'content.jobs.new', label: 'New job button', page: '/app/content/jobs', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'content.jobs.form.type', label: 'Job type select', page: '/app/content/jobs', kind: 'input', actions: ['select', 'highlight'] },
  { id: 'content.jobs.form.prompt', label: 'Job prompt field', page: '/app/content/jobs', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'content.jobs.form.submit', label: 'Save job button', page: '/app/content/jobs', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'content.projects.new', label: 'New content project button', page: '/app/content/projects', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'content.projects.form.name', label: 'Content project name field', page: '/app/content/projects', kind: 'input', actions: ['type', 'highlight'] },
  { id: 'content.projects.form.submit', label: 'Save content project button', page: '/app/content/projects', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'marketplace.new', label: 'New marketplace entry button', page: '/app/marketplace', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'marketplace.sales.add-period', label: 'Add sales period button', page: '/app/marketplace/sales', kind: 'button', actions: ['click', 'highlight'] },
  { id: 'marketplace.sales.form.submit', label: 'Save sales period button', page: '/app/marketplace/sales', kind: 'button', actions: ['click', 'highlight'] },
];

const _TARGET_INDEX = new Map<string, UiTargetDescriptor>(UI_TARGETS.map((t) => [t.id, t]));

/** Exact descriptor, or the wildcard family descriptor for dynamic ids. */
export function targetById(id: string): UiTargetDescriptor | undefined {
  const direct = _TARGET_INDEX.get(id);
  if (direct) return direct;
  const family = id.slice(0, id.lastIndexOf('.'));
  return family ? _TARGET_INDEX.get(`${family}.*`) : undefined;
}

/** Resolve a target DOM node NOW (lazy — never cache elements across renders).
 *  Fallback chain: data-agent-id → descriptor.selector → auto-derived id.
 *
 *  The auto step matters: pages are NOT hand-tagged, so the agent could not
 *  reach controls like the Opportunity Scanner's Refresh button.  `resolveAutoId`
 *  re-derives ids from the live DOM on demand (see `agentTag.ts`). */
export function resolveAgentId(id: string): HTMLElement | null {
  const direct = document.querySelector<HTMLElement>(
    `[data-agent-id="${cssEscape(id)}"]`,
  );
  if (direct) return direct;
  const descriptor = targetById(id);
  if (descriptor?.selector) {
    const fallback = document.querySelector<HTMLElement>(descriptor.selector);
    if (fallback) return fallback; // degraded resolution (logged upstream)
  }
  // Auto-derived ids: tag the current screen, then retry the lookup.
  const auto = resolveAutoId(id);
  if (auto) return auto;
  const retried = document.querySelector<HTMLElement>(`[data-agent-id="${cssEscape(id)}"]`);
  if (retried) return retried;
  // LAST RESORT (owner report 2026-09-21): the agent may reference a control by
  // the name the user used ("the Stakeholder Demo event") rather than by an
  // auto-derived id it cannot be expected to reproduce exactly. Resolve by
  // visible label / accessible name, uniquely — ambiguity is refused rather than
  // guessed, so a mis-click can never land on the wrong control.
  return resolveByLabel(id);
}

/** Find a rendered control whose visible name matches *label*.
 *
 *  Ambiguity handling (owner report 2026-09-21): the SAME event legitimately
 *  renders twice — once in the month grid (`calendar.event.<id>`) and once in the
 *  upcoming list (`calendar.list.event.<id>`) — with an identical accessible name.
 *  Refusing every ambiguous match made "open the event" impossible.  When the
 *  candidates all refer to the SAME underlying thing we accept the first VISIBLE
 *  one; a genuinely ambiguous match still resolves to nothing, so a mis-click can
 *  never land on the wrong control.
 */
export function resolveByLabel(label: string): HTMLElement | null {
  const needle = (label || '').trim().toLowerCase();
  if (needle.length < 3) return null; // too short to be safe
  const slug = needle.replace(/^auto\./, '').split('.').pop() || '';
  const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-agent-id]'));
  const visible = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    return rect.width >= 1 && rect.height >= 1;
  };
  const nameOf = (el: HTMLElement) =>
    (el.getAttribute('aria-label') || el.textContent || '').trim().toLowerCase();
  const matches = nodes.filter((el) => {
    const id = (el.getAttribute('data-agent-id') || '').toLowerCase();
    return nameOf(el) === needle || id === needle || (slug.length >= 4 && id.includes(slug));
  });
  const onScreen = matches.filter(visible);
  const pool = onScreen.length ? onScreen : matches;
  if (pool.length === 0) return null;
  if (pool.length === 1) return pool[0];
  // Several matches: accept only if they all target the same underlying record
  // (e.g. the same event shown in two places). Otherwise it is ambiguous.
  const keys = new Set(
    pool.map((el) => {
      const id = el.getAttribute('data-agent-id') || '';
      // `calendar.list.event.<id>` / `calendar.detail.event.<id>` -> `<id>`
      const parts = id.split('.');
      const last = parts[parts.length - 1] || id;
      return /^[0-9a-f-]{8,}$/i.test(last) ? last : id;
    }),
  );
  if (keys.size === 1) {
    // Prefer the primary (non-list/detail) one when both are present.
    return (
      pool.find((el) => !/(^|\.)(list|detail)\./.test(el.getAttribute('data-agent-id') || '')) || pool[0]
    );
  }
  return null;
}

/** Thrown by waitForAgentId when a target never renders. */
export class UiTargetMissingError extends Error {
  constructor(public readonly agentId: string) {
    super(`UI target not found: ${agentId}`);
    this.name = 'UiTargetMissingError';
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Wait (poll + one scroll retry) for a target to appear in the DOM.
 *  Spec §7: retry once (scroll) after half the budget, then fail. */
export async function waitForAgentId(
  id: string,
  timeoutMs = 3000,
): Promise<HTMLElement> {
  const startedAt = Date.now();
  let scrollRetried = false;
  for (;;) {
    const el = resolveAgentId(id);
    if (el) return el;
    const elapsed = Date.now() - startedAt;
    if (!scrollRetried && elapsed >= timeoutMs * 0.5) {
      // One scroll nudge: some layouts only reveal controls after a scroll.
      scrollRetried = true;
      window.dispatchEvent(new Event('scroll'));
    }
    if (elapsed >= timeoutMs) throw new UiTargetMissingError(id);
    await sleep(50);
  }
}

/** Serialisable capability manifest for the planner (P2) — no DOM references. */
export function capabilityManifest() {
  return {
    v: 1,
    targets: UI_TARGETS.map(({ id, label, page, kind, actions, dynamic, dangerous }) => ({
      id,
      label,
      page,
      kind,
      actions,
      dynamic: Boolean(dynamic),
      dangerous: Boolean(dangerous),
    })),
  };
}

/** Escape a value for safe use inside an attribute selector. */
function cssEscape(value: string): string {
  return (window.CSS && CSS.escape) ? CSS.escape(value) : value.replace(/"/g, '\\"');
}

export const isUiStepKind = (kind: string): kind is UiStepKind =>
  kind === 'navigate' || kind === 'click' || kind === 'highlight' || kind === 'scrollTo'
  || kind === 'type' || kind === 'select' || kind === 'request' || kind === 'today';